import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { reportApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import BibleProgressCalendar from '../components/progress/BibleProgressCalendar';
import GroupProgress from '../components/progress/GroupProgress';
import ChallengeGoals from '../components/progress/ChallengeGoals';
import LiveTicker from '../components/progress/LiveTicker';
import RemainingChapters from '../components/progress/RemainingChapters';
import BookCompletionMeters from '../components/progress/BookCompletionMeters';
import { bibleCompletionGoal } from '../utils/communityBooks';
import './HomePage.css';

/**
 * Home is the community read-through.
 *
 * The score states where the community is, and the map shows it chapter by
 * chapter with the still-needed chapters highlighted. Logging lives in the
 * profile menu, so this page opens on community progress.
 */
const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const { lastGlobalStatsUpdate, lastReadingReportCreated } = useWebSocket();
  const [globalStats, setGlobalStats] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRemaining, setShowRemaining] = useState(false);
  const loadStatsTimeoutRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const CACHE_DURATION = 300000; // 5 minutes cache duration

  const loadGlobalStats = useCallback(async (retryCount = 0, { force = false } = {}) => {
    try {
      // Check if we have recent data to avoid unnecessary API calls
      const now = Date.now();
      if (!force && lastFetchTimeRef.current && (now - lastFetchTimeRef.current) < CACHE_DURATION) {
        console.log('Using cached global stats');
        setLoading(false);
        return;
      }

      const response = await reportApi.getGlobalStats();
      setGlobalStats(response.data.stats);
      lastFetchTimeRef.current = now;
      setError(null);
    } catch (err) {
      console.error('Failed to load global stats:', err);

      // Handle rate limiting with exponential backoff
      if (err.response?.status === 429 && retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 3000; // 3s, 6s
        console.log(`Rate limited, retrying in ${delay}ms...`);
        setTimeout(() => {
          loadGlobalStats(retryCount + 1, { force });
        }, delay);
        return;
      }

      // If we've exhausted retries or it's not a rate limit error, show error
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait a moment and try again.');
      } else {
        setError('Failed to load statistics. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompletion = useCallback(async () => {
    try {
      const response = await reportApi.getNextCompletionChapters();
      setCompletion(response.data);
    } catch (err) {
      // Not critical — the hero still works from global stats.
      console.error('Failed to load next completion chapters:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadGlobalStats();
    loadCompletion();

    return () => {
      if (loadStatsTimeoutRef.current) {
        clearTimeout(loadStatsTimeoutRef.current);
      }
    };
  }, [loadGlobalStats, loadCompletion]);

  // Live stats: a log by anyone repaints the map without a reload.
  useEffect(() => {
    if (lastGlobalStatsUpdate?.stats) {
      setGlobalStats(lastGlobalStatsUpdate.stats);
      lastFetchTimeRef.current = Date.now();
    }
  }, [lastGlobalStatsUpdate]);

  // The still-needed list shifts as chapters get read; refresh it behind the
  // debounced stats broadcast rather than on every single report event.
  useEffect(() => {
    if (!lastReadingReportCreated) return undefined;
    const timer = setTimeout(loadCompletion, 2500);
    return () => clearTimeout(timer);
  }, [lastReadingReportCreated, loadCompletion]);

  const neededChapterIds = useMemo(() => {
    if (!completion?.chaptersNeeded) return null;
    return new Set(completion.chaptersNeeded.map(chapter => chapter.id));
  }, [completion]);

  const retry = () => {
    if (loadStatsTimeoutRef.current) {
      clearTimeout(loadStatsTimeoutRef.current);
    }
    loadStatsTimeoutRef.current = setTimeout(() => {
      loadGlobalStats(0, { force: true });
    }, 1000);
  };

  if (loading) {
    return (
      <div className="home-page loading">
        <LoadingSpinner size="large" message="Loading statistics..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-page error">
        <div className="error-message">
          <h2>Unable to load statistics</h2>
          <p>{error}</p>
          <button onClick={retry} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const challenge = globalStats?.challenge || completion?.challenge || null;

  return (
    <div className="home-page">
      {/* Chapters against the 75,000 goal, season readers, and Bible completions */}
      <GroupProgress stats={globalStats} challenge={challenge} />

      {/* The community's Bible, chapter by chapter */}
      <div className="global-progress-section">
        <div className="section-header">
          <h2>{challenge ? challenge.name : 'Community Bible Reading Progress'}</h2>
          <p>
            {neededChapterIds && neededChapterIds.size > 0
              ? `${neededChapterIds.size} highlighted chapters are all that stand between us and reading the Bible through again.`
              : 'See which chapters of the Bible have been read by our community'}
          </p>
          {neededChapterIds && neededChapterIds.size > 0 && (
            <button
              type="button"
              className="remaining-toggle"
              aria-expanded={showRemaining}
              aria-controls="remaining-chapters"
              onClick={() => setShowRemaining(open => !open)}
            >
              {showRemaining
                ? 'Hide the list'
                : `Show only the ${neededChapterIds.size} remaining`}
            </button>
          )}
          <Link to="/replay" className="replay-link">
            ▶ Watch the season
          </Link>
        </div>

        {showRemaining && (
          <div id="remaining-chapters">
            <RemainingChapters chaptersNeeded={completion?.chaptersNeeded} />
          </div>
        )}

        {globalStats?.chapters && globalStats?.chapterReadCounts && (
          <BibleProgressCalendar
            chapters={globalStats.chapters}
            chapterReadCounts={globalStats.chapterReadCounts}
            neededChapterIds={neededChapterIds}
          />
        )}
      </div>

      {/* Each book's full read-throughs against the complete-Bible goal */}
      {globalStats?.chapters && globalStats?.chapterReadCounts && (
        <BookCompletionMeters
          chapters={globalStats.chapters}
          chapterReadCounts={globalStats.chapterReadCounts}
          goal={bibleCompletionGoal(globalStats)}
        />
      )}

      {/* Season goals, measured inside the challenge window */}
      {challenge && globalStats?.challengeGoals?.length > 0 && (
        <ChallengeGoals
          goals={globalStats.challengeGoals}
          challengeName={challenge.name}
        />
      )}

      <LiveTicker />

      {/* Additional CTA for unauthenticated users */}
      {!isAuthenticated && (
        <div className="bottom-cta-section">
          <div className="cta-content">
            <h2>Ready to start your Bible reading journey?</h2>
            <p>Join thousands of readers who are growing in their faith together</p>
            <Link to="/auth?view=register" className="cta-signup-button large">
              Create an account
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
